import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@carbon/react";
import { Add, Upload } from "@carbon/icons-react";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import DeliveriesTable from "../../components/DeliveriesTable/DeliveriesTable";
import DeliveryStateFilter from "../../components/DeliveriesTable/DeliveryStateFilter";
import { PAGE_SIZES } from "../../components/carbon/DataTable/DataTable";
import { NetworkError } from "../../components";
import { useDeliveries, useUpdateDelivery } from "../../hooks/useDeliveries";
import { queryKeys } from "../../hooks/queryKeys";
import { useNotification } from "../../context";
import { DeliveryState } from "../../types";
import "./deliveries.scss";

/**
 * Buyer deliveries list — one Carbon `DataTable` with a state filter `Dropdown` in its toolbar
 * (mirrors `PurchaseOrdersPage`), replacing the previous Carbon `Tabs`. "All" (the default) hits
 * the all-states endpoint; a chosen state hits the per-state one. Filtering and pagination are both
 * server-side (`useDeliveries`) — rows are never re-filtered client-side.
 */
const Deliveries = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  // undefined = the "All" option.
  const [state, setState] = useState<DeliveryState | undefined>(undefined);

  const {
    data: deliveries,
    isLoading: isLoadingDeliveries,
    error,
  } = useDeliveries(state, page, pageSize);

  const { mutate: updateDelivery, isPending } = useUpdateDelivery(() => {
    openNotification(t("deliveries.success"));
    queryClient.invalidateQueries({ queryKey: [queryKeys.DELIVERIES] });
  });

  const stateFilter = (
    <DeliveryStateFilter
      value={state}
      onChange={(next) => {
        setState(next);
        // A new filter always restarts at the first page.
        setPage(0);
      }}
    />
  );

  const actions = (
    <div className="deliveries__header-actions">
      <Button kind="tertiary" as={Link} to="/deliveries/bulk" renderIcon={Upload}>
        {t("deliveries.massive.new")}
      </Button>
      <Button as={Link} to="/deliveries/new" renderIcon={Add}>
        {t("deliveries.new")}
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader title={t("navigation.delivery")} action={actions} />

      <div className="deliveries__filters">{stateFilter}</div>

      <div className="deliveries__tab-content">
        {error ? (
          <NetworkError />
        ) : (
          <DeliveriesTable
            deliveries={deliveries}
            pageNumber={page}
            setPageNumber={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isLoading={isLoadingDeliveries || isPending}
            updateDelivery={updateDelivery}
            emptyTitle={t("deliveries.filter.empty")}
          />
        )}
      </div>
    </div>
  );
};

export default Deliveries;
